import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { safeReturnTo } from '../auth/return-to';
import { Card } from '../components/ui';
import './LoginPage.css';

let capturedLocation: string | null = null;
let capturedToken: string | null = null;
let redemption: { token: string; promise: Promise<void> } | null = null;

function consumeFragmentToken(): string | null {
  const location = `${window.location.pathname}${window.location.search}`;
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
  if (token !== null) {
    capturedLocation = location;
    capturedToken = token;
    window.history.replaceState(window.history.state, '', location);
  }
  return capturedLocation === location ? capturedToken : null;
}

function redeemOnce(token: string, verifyMagicLink: (value: string) => Promise<void>) {
  if (redemption?.token === token) return redemption.promise;
  const promise = verifyMagicLink(token);
  redemption = { token, promise };
  return promise;
}

export function MagicLinkVerifyPage() {
  const { status, verifyMagicLink } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(consumeFragmentToken);
  const [failed, setFailed] = useState(false);
  const returnTo = safeReturnTo(location.search);

  useEffect(() => {
    if (!token || token.length < 16 || token.length > 512) {
      setFailed(true);
      return;
    }
    let active = true;
    void redeemOnce(token, verifyMagicLink)
      .then(() => {
        if (active) navigate(returnTo, { replace: true });
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [navigate, returnTo, token, verifyMagicLink]);

  if (status === 'authenticated') {
    return <Navigate to={returnTo} replace />;
  }

  return (
    <main className="login-page">
      <Card className="login-card login-card--status">
        <div className="login-brand">ZoloTalk</div>
        <h1>{failed ? 'This link cannot be used' : 'Signing you in…'}</h1>
        <p role={failed ? 'alert' : 'status'}>
          {failed
            ? 'The link is invalid or expired. Return to sign in and request a new one.'
            : 'Verifying your secure, one-time link.'}
        </p>
        {failed && <a href="/login">Return to sign in</a>}
      </Card>
    </main>
  );
}
